"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import TerminalTextLine from "./TerminalTextLine";

export type BootKind = "ssh" | "install" | "desktop" | "script";
const packages = [
  "libportfolio-core", "libaccent-runtime", "libcommand-parser", "libkeyboard-map",
  "libproject-index", "libimage-cache", "libcase-study", "libresume-viewer",
  "libclipboard-bridge", "libtheme-sync", "libdesk-events", "libnotebook",
  "project-browser", "colour-studio", "contact-agent", "portfolio-session",
  "portfolio-desktop",
];
const commands = {
  script: "./install.sh",
  ssh: "ssh guest@sahiya.lk",
  install: "sudo apt install portfolio-desktop",
  desktop: "startx",
};
export function terminalOutput(kind: BootKind): string[] {
  if (kind === "script") return [
    "[install.sh] Checking package manager...",
    "[install.sh] Refreshing package index...",
    "$ sudo apt-get update",
    "Hit:1 portfolio:/stable InRelease",
    "Reading package lists... Done",
    "[install.sh] Installing portfolio-desktop...",
    "$ sudo apt-get install --yes --no-install-recommends portfolio-desktop",
    ...terminalOutput("install"),
  ];
  if (kind === "ssh") return [
    "Connecting to sahiya.lk...",
    "Connection established.",
    "Authenticated to sahiya.lk as guest.",
    "",
    "Welcome to Sahindu Gayanuka’s workspace.",
    "",
    "  Projects     ~/projects",
    "  Profile      ~/about.txt",
    "  Notebook     ~/notebook.txt",
    "",
    "Run ls to explore your home directory.",
  ];
  if (kind === "install") return [
    "Reading package lists... Done",
    "Building dependency tree... Done",
    "Reading state information... Done",
    "The following additional packages will be installed:",
    ...packages.slice(0, -1).map(name => "  " + name),
    "0 upgraded, 17 newly installed, 0 to remove.",
    "Need to get 8,192 kB of archives.",
    "After this operation, the visual workspace will be available.",
    ...packages.map((name, index) => `Get:${index + 1} portfolio:/stable ${name} 1.0.0 [${192 + index * 37} kB]`),
    "Fetched 8,192 kB",
    "Extracting templates from packages: 100%",
    "Preconfiguring packages...",
    ...packages.flatMap((name, index) => [
      `Selecting previously unselected package ${name}.`,
      `(Reading database ... ${Math.round((index + 1) / packages.length * 100)}%)`,
      `Preparing to unpack .../${name}_1.0.0_all.deb ...`,
      `Unpacking ${name} (1.0.0) ...`,
    ]),
    ...packages.map(name => `Setting up ${name} (1.0.0) ...`),
    "Processing triggers for portfolio-session (1.0.0) ...",
    "Created session link: portfolio-desktop → /workspace/startx",
    "[ OK ] Project index registered.",
    "[ OK ] Colour studio connected to the site accent.",
    "Installation complete. Run startx to launch your workspace.",
  ];
  return [
    "X.Org Portfolio Display Server",
    "(==) Using session configuration: /etc/portfolio/session.conf",
    "(II) Loading extension: project-browser",
    "(II) Loading extension: colour-studio",
    "(II) Loading extension: resume-viewer",
    "(II) Initialising keyboard: history, completion, shortcuts",
    "(II) Connecting shared accent bus... connected",
    ...["project-index", "image-cache", "command-router", "clipboard-bridge", "theme-sync", "desk-events", "resume-viewer", "portfolio-desktop"].flatMap(name => [
      `[ .... ] Starting ${name}.service ...`,
      `[  OK  ] Started ${name}.service.`,
    ]),
    "[ OK ] Reached target: graphical-session.target",
    "(II) Mounting visual workspace...",
    "(II) First frame ready. Handing over session.",
  ];
}

export default function TerminalBoot({ kind, onDone, onCancel }: {
  kind: BootKind; onDone: () => void; onCancel: () => void;
}) {
  const [count, setCount] = useState(0);
  const scroll = useRef<HTMLDivElement>(null);
  const callbacks = useRef({ onDone, onCancel });
  callbacks.current = { onDone, onCancel };
  const lines = useMemo(() => terminalOutput(kind), [kind]);
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let current = 0;
    let timer: ReturnType<typeof setTimeout>;
    // A terminal receives output in chunks, rather than typing each line.
    const between = (min: number, max: number) => min + Math.random() * (max - min);
    const nextChunk = () => {
      if (kind === "ssh") {
        if (current === 0) return { size: 1, delay: between(500, 950) };
        if (current === 1) return { size: 1, delay: between(180, 420) };
        if (current === 2) return { size: 1, delay: between(240, 500) };
        if (current === 3) return { size: 2, delay: between(550, 850) };
        if (current === 5) return { size: 2, delay: between(320, 500) };
        if (current === 7) return { size: 1, delay: between(130, 230) };
        if (current === 8) return { size: 1, delay: between(180, 300) };
        return { size: lines.length - current, delay: between(460, 700) };
      }
      const next = lines[current] || "";
      if (current === 0 && kind === "script")
        return { size: 2, delay: between(220, 460) };
      if (next.startsWith("  ")) {
        let end = current;
        while (lines[end]?.startsWith("  ")) end++;
        return { size: end - current, delay: between(15, 45) };
      }
      if (/Reading package|Building dependency|Preconfiguring|Processing triggers|Mounting|Connecting/.test(next))
        return { size: 1, delay: between(350, 750) };
      if (next.startsWith("Get:"))
        return { size: Math.floor(between(2, 6)), delay: Math.random() < .2 ? between(300, 600) : between(35, 130) };
      if (next.startsWith("Selecting"))
        return { size: 4, delay: Math.random() < .18 ? between(240, 480) : between(35, 110) };
      if (next.startsWith("Setting up"))
        return { size: Math.floor(between(1, 4)), delay: between(60, 220) };
      if (next.includes("Starting ") && next.includes(".service"))
        return { size: 1, delay: between(40, 100) };
      if (next.includes("Started ") && next.includes(".service"))
        return { size: 1, delay: between(120, 430) };
      return { size: Math.floor(between(1, 4)), delay: between(20, 100) };
    };
    const tick = () => {
      const chunk = nextChunk();
      timer = setTimeout(() => {
        current = Math.min(lines.length, current + chunk.size);
        setCount(current);
        if (current === lines.length) {
          timer = setTimeout(() => callbacks.current.onDone(), kind === "ssh" ? 500 : 320);
        } else tick();
      }, chunk.delay);
    };
    setCount(0);
    if (reduced) { setCount(lines.length); timer = setTimeout(() => callbacks.current.onDone(), 150); }
    else tick();
    const interrupt = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "c" &&
          event.target instanceof Element && event.target.closest(".floating-terminal:not([hidden])")) {
        event.preventDefault(); callbacks.current.onCancel();
      }
    };
    window.addEventListener("keydown", interrupt, true);
    return () => { clearTimeout(timer); window.removeEventListener("keydown", interrupt, true); };
  }, [kind, lines.length]);
  useEffect(() => {
    if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight;
  }, [count]);
  useEffect(() => {
    const window = scroll.current?.closest(".floating-terminal");
    if (window && !window.hasAttribute("hidden")) {
      scroll.current?.closest(".terminal-boot")?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
    }
  }, []);
  return <section className="terminal-boot" aria-label={kind === "install" ? "Installing portfolio workspace" : "Starting portfolio session"} aria-busy="true">
    <div className="terminal-boot-scroll" ref={scroll} aria-hidden="true">
      <p className="legacy-echo"><span>guest@sahiya.lk:~$</span> {commands[kind]}</p>
      {lines.slice(0, count).map((line, index) => <div key={index} className={kind === "ssh" ? "boot-ssh-row" : line.includes("OK") || line.startsWith("Setting up") ? "boot-ok" : ""}><TerminalTextLine line={line} /></div>)}
      <span className="terminal-block">▌</span>
    </div>
    {kind === "desktop" && <div className="console-build-wireframe boot-wireframe" aria-hidden="true"><i /><div><i /><i /><i /><i /></div><b /></div>}
    <footer className="legacy-footer"><span role="status">{kind === "ssh" ? count < 2 ? "Connecting…" : "Opening shell…" : (kind === "install" || kind === "script" ? "Installing packages" : "Starting session") + " · " + Math.round(count / lines.length * 100) + "%"}</span><button onClick={onCancel}>Cancel · Ctrl+C</button></footer>
  </section>;
}
